import { Button } from '@/components/ui/button';
import { useMutation } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface DirectUpdateButtonProps {
  installationId?: string | number;
  formData: any;
  disabled?: boolean;
}

export default function DirectUpdateButton({
  installationId,
  formData,
  disabled,
}: DirectUpdateButtonProps) {
  const { toast } = useToast();

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!installationId) {
        throw new Error('Installation ID is required');
      }

      // Transform date fields from strings to Date objects
      const dateFields = [
        'contractSigned',
        'scheduledDate',
        'completedDate',
        'sevenDayPaymentDate',
        'finalPaymentDate',
        'dnoApplicationSent',
        'dnoApproved',
        'commissioningDate',
        'additionalPaymentDate',
        'installationDate',
      ];

      const transformedData = { ...formData };

      // Convert date strings to Date objects
      dateFields.forEach((field) => {
        if (transformedData[field] && typeof transformedData[field] === 'string') {
          transformedData[field] = new Date(transformedData[field]);
        }
      });

      // Log what we're sending
      console.log('Direct update - sending data:', transformedData);

      // Use PATCH method which has more lenient validation
      const response = await apiRequest(
        'PATCH',
        `/api/installations/${String(installationId)}`,
        transformedData
      );

      return response;
    },
    onMutate: () => {
      toast({
        title: 'Updating...',
        description: 'Updating installation...',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Installation updated successfully',
      });

      // Reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      console.error('Update error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update installation',
        variant: 'destructive',
      });
    },
  });

  return (
    <Button
      type="button"
      onClick={() => updateMutation.mutate()}
      disabled={disabled || updateMutation.isPending}
      className="w-full"
    >
      {updateMutation.isPending ? 'Updating...' : 'Update Installation'}
    </Button>
  );
}
